/**
 * ItemBillPage.tsx — G3a: the bill page an Audit item was read from, in ONE component used by
 * every item table on «Audit & Discrepanze» (the category tree and flat views, «Voci
 * principali», «Azioni richieste»).
 *
 * «p. 84» / «p. 84–85» are PHYSICAL pages of the PDF file (the sheet index, the same
 * convention as «pagina PDF» elsewhere), never the number printed on the sheet — the title says
 * so. Clicking opens the stored bill at the first page in a new tab (`useBillPageOpener`).
 *
 * Four states, four renderings, because they are four different facts:
 *  - no `pages` prop: the table is used outside the Audit page — nothing, as before G3a;
 *  - loading: nothing yet — «non registrata» before the answer arrives would be a guess;
 *  - the request failed: «pagina non disponibile» — NOT «non registrata», which would claim
 *    the analysis recorded no page when the screen simply could not ask;
 *  - answered: the page, or «pagina non registrata» for an item the answer does not list.
 */
import { type LineItemPageSpan } from "../../lib/tenderApi";
import { useBillPageOpener } from "../../lib/sourceOpener";

export type ItemPagesView =
  | { state: "loading" }
  | { state: "failed" }
  | { state: "ready"; pages: Map<string, LineItemPageSpan> };

const NOTE_STYLE = { fontSize: 11, whiteSpace: "nowrap" } as const;

/** «p. 84» or «p. 84–85» (en dash). */
export function itemPagesLabel(span: LineItemPageSpan): string {
  return span.pageEnd > span.pageStart
    ? `p. ${span.pageStart}–${span.pageEnd}`
    : `p. ${span.pageStart}`;
}

export function ItemBillPage({
  itemId,
  pages,
}: {
  itemId: string;
  pages?: ItemPagesView;
}): JSX.Element | null {
  if (!pages || pages.state === "loading") return null;
  if (pages.state === "failed") {
    return (
      <span
        data-testid="item-bill-page-unavailable"
        className="muted"
        title="Le pagine del computo non sono state caricate: ricarica la pagina per riprovare."
        style={NOTE_STYLE}
      >
        pagina non disponibile
      </span>
    );
  }
  const span = pages.pages.get(itemId);
  if (!span) {
    return (
      <span data-testid="item-bill-page-missing" className="muted" style={NOTE_STYLE}>
        pagina non registrata
      </span>
    );
  }
  return <BillPageLink itemId={itemId} span={span} />;
}

/** Split out so the opener's hooks run only where there is something to open. */
function BillPageLink({ itemId, span }: { itemId: string; span: LineItemPageSpan }): JSX.Element {
  const { open, error, busy } = useBillPageOpener();
  return (
    <>
      <button
        type="button"
        data-testid="item-bill-page"
        title={`Apri il computo alla pagina ${span.pageStart} del file PDF`}
        aria-busy={busy || undefined}
        onClick={(e) => {
          // The row's own click opens its discrepancy; this one opens the bill.
          e.stopPropagation();
          void open(itemId, span.pageStart);
        }}
        style={{
          appearance: "none",
          border: "none",
          background: "transparent",
          padding: 0,
          cursor: busy ? "progress" : "pointer",
          color: "var(--c-brand)",
          textDecoration: "underline",
          ...NOTE_STYLE,
        }}
      >
        {itemPagesLabel(span)}
      </button>
      {error && (
        <span
          data-testid="item-bill-page-error"
          role="alert"
          style={{ fontSize: 11, color: "var(--c-err)" }}
        >
          {error}
        </span>
      )}
    </>
  );
}
